import {
  CheckCircleOutlined,
  DeleteOutlined,
  InfoCircleFilled,
  InfoCircleOutlined,
  WarningFilled,
} from "@ant-design/icons";
import { Modal } from "antd";
import { IActionButtonProps } from "components/generalComponents/actionButtonComponent/interface/IActionButtonComponent";
import { isEmpty } from "lodash";
import { CSSProperties, ReactNode } from "react";
import { FaRegTrashAlt } from "react-icons/fa";
import { LuInfo } from "react-icons/lu";
import { formatCurrency } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";
import { colorFormat } from "./ColorFormat";

export class ActionFormat {
  static renderActions = (data: {
    keyword: string;
    renderType: "detail" | "delete" | "approve";
    label?: string;
    loading?: boolean;
    color?: string;
    icon?: ReactNode;
    needFollowingFeature?: boolean;
    onClick: () => void | Promise<void> | unknown;
  }) => {
    const {
      keyword,
      renderType,
      color,
      icon,
      label,
      loading,
      needFollowingFeature = false,
      onClick,
    } = data;

    if (
      needFollowingFeature === false ||
      (!isEmpty(keyword) && needFollowingFeature === true)
    ) {
      switch (renderType) {
        case "detail":
          return {
            className: "font-semibold mr-1",
            type: "link",
            loading: loading || false,
            icon: icon ?? (
              <LuInfo style={{ color: colorFormat.Blue }} size={18} />
            ),
            label: label || "",
            onClick: onClick,
            colorPrimary: color,
          } as IActionButtonProps;
        case "delete":
          return {
            className: "font-semibold text-red-700 ml-1",
            type: "link",
            icon: <FaRegTrashAlt size={18} />,
            label: label || "",
            onClick: onClick,
            colorPrimary: color,
          } as IActionButtonProps;
        case "approve":
          return {
            className: "font-semibold text-green-700 border-green-700",
            type: "default",
            icon: <CheckCircleOutlined style={{ fontSize: "18px" }} />,
            label: label || "",
            onClick: onClick,
            colorPrimary: color,
          } as IActionButtonProps;
      }
    } else {
      return undefined;
    }
  };

  static confirmedModalRendering = async (data: {
    handleDelete: () => Promise<void>;
    approveAsking?: boolean;
    custom?: boolean;
    title?: string | ReactNode;
    width?: number;
    style?: CSSProperties;
  }) => {
    return Modal.confirm({
      icon: null,
      style: data?.style,
      modalRender: (modal) => (
        <div>
          {modal}
          <style>
            {`
          .ant-modal-confirm .ant-modal-confirm-btns {
            display: flex;
            justify-content: center !important;
          }
        `}
          </style>
        </div>
      ),
      title: (
        <div className="flex flex-col items-center text-center justify-center break-words gap-y-3">
          {data?.approveAsking === true || data?.custom === true ? (
            <InfoCircleFilled
              style={{
                fontSize: "38px",
                color: "blue",
              }}
            />
          ) : (
            <WarningFilled
              style={{
                fontSize: "38px",
                color: colorFormat.Red_Dark,
              }}
            />
          )}
          <p className="text-black text-[18px]">
            {data?.approveAsking === true
              ? strings?.GlobalMessage.ApproveAksing
              : data?.custom === true
                ? data?.title
                : strings.GlobalMessage.DeleteConfirmation}
          </p>
          {data?.custom === true ? (
            <></>
          ) : (
            <p className="italic text-gray-500 text-[16px]">
              {!!data?.title ? data?.title : ""}
            </p>
          )}
        </div>
      ),
      okText: strings.GlobalLabels.Confirmed,
      cancelText: strings.GlobalLabels.Cancel,
      okButtonProps: {
        danger: data?.approveAsking === true ? false : true,
        icon:
          data?.approveAsking === true ? (
            <InfoCircleOutlined />
          ) : (
            <DeleteOutlined />
          ),
      },
      destroyOnClose: true,
      centered: true,
      onOk: async () => data?.handleDelete(),
      width: !!data?.width && data?.width,
    });
  };

  static warningLabel = (isAllowed: boolean) => {
    if (!isAllowed) {
      return (
        <span className="text-red-500 italic">
          ({strings.GlobalMessage.NotAllowedToUse})
        </span>
      );
    }
  };

  static statusForContent = (value: number, localStrings: typeof strings) => {
    switch (value) {
      case 0:
        return (
          <span className="text-green-600 font-bold">
            {localStrings.GlobalLabels.Active}
          </span>
        );
      case 1:
        return (
          <span className="text-red-700 font-bold">
            {localStrings.GlobalLabels.Inactive}
          </span>
        );
      default:
        return "";
    }
  };

  static statusForDetails = (value: number, localStrings: typeof strings) => {
    switch (value) {
      case 0:
        return localStrings.TransactionHistory.Status.Init;
      case 1:
        return localStrings.TransactionHistory.Status.Progerssing;
      case 2:
        return localStrings.TransactionHistory.Status.Finished;
      case 3:
        return localStrings.TransactionHistory.Status.Error;
      case 4:
        return localStrings.TransactionHistory.Status.Deleted;
      case 5:
        return localStrings.TransactionHistory.Status.WaitingToBeConfirmed;
      default:
        return "";
    }
  };

  static colorTextDisplay(value: any, color: string) {
    return (
      <div style={{ color: color, fontWeight: "bold", fontStyle: "italic" }}>
        {value}
      </div>
    );
  }

  static productDetail(key: string, value: React.ReactNode) {
    switch (key) {
      case "PackagePrice":
        return value ? formatCurrency(String(value) || "0", false) : "";
      case "Storage":
        return `${value}GB`;
      case "Duration":
        return `${value} ${strings.GlobalLabels.Date}`;
      case "SimType":
        return value === "0"
          ? strings.SimManagement.SimType.ESim
          : strings.SimManagement.SimType.Physical;
      case "SimPrice":
        return value ? formatCurrency((value as number) || 0, false) : "";
      case "Price":
        return formatCurrency(String(value) || 0, false);
      default:
        return value;
    }
  }

  static checkIsUserReloadPage = () => {
    const [navigationEntry] = performance?.getEntriesByType?.(
      "navigation",
    ) as PerformanceNavigationTiming[];

    return navigationEntry?.type === "reload";
  };
}
